import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './VastAdPlayer.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';

const VastAdPlayer = ({ 
  adData,
  isVisible, 
  onAdComplete, 
  onAdSkip, 
  adHeight = 100,
  className = "",
  fireTracking
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [debugInfo, setDebugInfo] = useState('Initialisation...');
  
  // États de visibilité simplifiés
  const [isActuallyVisible, setIsActuallyVisible] = useState(false);
  const [impressionSent, setImpressionSent] = useState(false);
  
  const playerRef = useRef(null);
  const trackingFiredRef = useRef(new Set());
  const containerRef = useRef(null);
  const visibilityObserverRef = useRef(null);
  const autoplayAttemptedRef = useRef(false);
  
  // Constantes simplifiées
  const skipDelay = adData?.skipOffset || 5;
  const VISIBILITY_THRESHOLD = 0.7; // 70% de la pub doit être visible

  // URL vidéo déjà testée et prête par le manager
  const videoUrl = adData?.videoUrl;

  // Observer de visibilité
  useEffect(() => {
    if (!containerRef.current) return;

    // Nettoyer l'observer précédent
    if (visibilityObserverRef.current) {
      visibilityObserverRef.current.disconnect();
    }

    // Créer un nouvel observer
    visibilityObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isCurrentlyVisible = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;
        
        console.log(`👁️ VISIBILITÉ PUB ${adData?.id}:`, {
          intersecting: entry.isIntersecting,
          ratio: (entry.intersectionRatio * 100).toFixed(1) + '%',
          visible: isCurrentlyVisible,
          threshold: (VISIBILITY_THRESHOLD * 100).toFixed(0) + '%'
        });
        
        const wasVisible = isActuallyVisible;
        setIsActuallyVisible(isCurrentlyVisible);
        
        if (isCurrentlyVisible && !wasVisible) {
          // Début de visibilité
          console.log(`✅ PUB ${adData?.id} DEVIENT VISIBLE`);
          setDebugInfo(prev => prev + ' | Visible: OUI');
          
        } else if (!isCurrentlyVisible && wasVisible) {
          // Fin de visibilité
          console.log(`❌ PUB ${adData?.id} N'EST PLUS VISIBLE`);
          setDebugInfo(prev => prev + ' | Visible: NON');
          
          // Stopper la lecture si plus visible
          if (isPlaying) {
            console.log(`⏸️ PAUSE PUB ${adData?.id} - Plus visible`);
            setIsPlaying(false);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.7, 0.9, 1.0]
      }
    );

    visibilityObserverRef.current.observe(containerRef.current);

    return () => {
      if (visibilityObserverRef.current) {
        visibilityObserverRef.current.disconnect();
      }
    };
  }, [adData?.id, isActuallyVisible, isPlaying]);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      const mobile = isMobileDevice || (isSmallScreen && hasTouchScreen);
      setIsMobile(mobile);
      setDebugInfo(prev => prev + ` | Mobile: ${mobile}`);
      
      return mobile;
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Log initial
  useEffect(() => {
    if (adData) {
      const info = {
        id: adData.id,
        videoUrl: !!adData.videoUrl,
        urlTested: adData.urlTested,
        duration: adData.duration,
        skipOffset: adData.skipOffset,
        format: adData.format,
        mobile: isMobile
      };
      
      console.log('🎬 INIT PLAYER PUB:', info);
      setDebugInfo(`Pub: ${adData.id} | URL: ${adData.urlTested ? 'Prête' : 'Non testée'} | Visible: NON`);
      
      if (!adData.videoUrl) {
        console.error('❌ Pas d\'URL vidéo fournie par le manager pour:', adData.id);
        setVideoError(true);
      }
    }
  }, [adData?.id, isMobile]);

  // Fire tracking simplifié - impression immédiate
  const fireTrackingEvent = useCallback((eventName, customUrls = null) => {
    if (!adData) return;
    
    const trackingKey = `${adData.id}_${eventName}`;
    
    if (trackingFiredRef.current.has(trackingKey)) {
      console.log(`🔄 TRACKING DÉJÀ ENVOYÉ ${eventName} pour pub ${adData.id}`);
      return;
    }
    
    let urls = customUrls;
    if (!urls && adData?.trackingEvents?.[eventName]) {
      urls = adData.trackingEvents[eventName];
    }
    
    if (urls && urls.length > 0) {
      console.log(`📊 TRACKING ${eventName.toUpperCase()} pour pub ${adData.id}`);
      try {
        fireTracking(urls, eventName);
        trackingFiredRef.current.add(trackingKey);
        setDebugInfo(prev => prev + ` | ${eventName}: ✅`);
      } catch (error) {
        console.error('❌ Erreur tracking:', error);
      }
    } else {
      console.log(`⚠️ Pas d'URLs tracking pour ${eventName} - pub ${adData.id}`);
    }
  }, [adData, fireTracking]);

  // Impression immédiate dès que la lecture commence
  useEffect(() => {
    if (adData && hasStarted && !impressionSent) {
      console.log('🎯 ENVOI IMPRESSION IMMÉDIAT - Pub démarrée:', adData.id);
      
      // Fire impression tracking URLs directement
      if (adData.impressions && adData.impressions.length > 0) {
        console.log(`📤 Envoi ${adData.impressions.length} impressions pour pub ${adData.id}`);
        fireTracking(adData.impressions, 'impression');
      }
      
      // Fire impression event
      fireTrackingEvent('impression');
      
      setImpressionSent(true);
      setDebugInfo(prev => prev + ' | Impression: ENVOYÉE');
    }
  }, [adData, hasStarted, impressionSent, fireTracking, fireTrackingEvent]);

  // Autoplay immédiat quand visible
  const attemptAutoplay = useCallback(() => {
    if (autoplayAttemptedRef.current || !isReady || !videoUrl || hasStarted || !isActuallyVisible) {
      const reason = !isActuallyVisible ? 'Pas visible' : 
                   !isReady ? 'Player pas prêt' : 
                   !videoUrl ? 'Pas d\'URL' : 
                   hasStarted ? 'Déjà démarré' : 'Autoplay déjà tenté';
      
      console.log(`🚫 AUTOPLAY BLOQUÉ pour pub ${adData?.id}: ${reason}`);
      return;
    }

    console.log('🎬 DÉMARRAGE AUTOPLAY IMMÉDIAT pour pub visible:', adData?.id);
    autoplayAttemptedRef.current = true;
    setDebugInfo(prev => prev + ' | Autoplay: Démarré');

    // Démarrage immédiat quand visible
    setTimeout(() => {
      if (isActuallyVisible) {
        setIsPlaying(true);
        setHasStarted(true);
        setMuted(false); // Démarrer avec le son
        setDebugInfo(prev => prev + ' | Lecture: ACTIVE');
      }
    }, isMobile ? 200 : 100);
  }, [isReady, videoUrl, hasStarted, adData?.id, isActuallyVisible]);

  // Déclencher autoplay dès que visible
  useEffect(() => {
    if (isReady && videoUrl && isActuallyVisible && !hasEnded && !hasStarted) {
      console.log(`🎯 CONDITIONS AUTOPLAY OK pour pub ${adData?.id}:`, {
        isReady,
        hasVideoUrl: !!videoUrl,
        isActuallyVisible,
        hasEnded,
        hasStarted
      });
      attemptAutoplay();
    }
  }, [isReady, videoUrl, isActuallyVisible, hasEnded, hasStarted, attemptAutoplay, adData?.id]);

  // Contrôle de la lecture selon visibilité
  useEffect(() => {
    if (!isActuallyVisible && isPlaying) {
      console.log(`⏸️ PAUSE - Pub ${adData?.id} plus visible`);
      setIsPlaying(false);
      
    } else if (isActuallyVisible && hasStarted && !hasEnded && isReady && videoUrl) {
      console.log(`▶️ PLAY - Pub ${adData?.id} visible`);
      setIsPlaying(true);
    }
  }, [isActuallyVisible, hasStarted, hasEnded, isReady, videoUrl, adData?.id, isPlaying]);

  // Fonction pour ouvrir les liens dans le navigateur par défaut
  const openInDefaultBrowser = (url) => {
    if (window.cordova && window.cordova.InAppBrowser) {
      window.cordova.InAppBrowser.open(url, '_system');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Gérer les clics
  const handleAdClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!adData || hasEnded) {
      console.log(`🚫 CLIC IGNORÉ pub ${adData?.id}: hasEnded=${hasEnded}`);
      return;
    }
    
    console.log('🖱️ CLIC PUB ACCEPTÉ:', adData.id);
    setDebugInfo(prev => prev + ' | Clic: ✅');
    
    // Fire click tracking
    if (adData.clickTracking && adData.clickTracking.length > 0) {
      fireTracking(adData.clickTracking, 'click');
    }
    
    fireTrackingEvent('click');
    fireTrackingEvent('clickTracking');
    
    // Ouvrir le lien
    if (adData.clickThrough) {
      openInDefaultBrowser(adData.clickThrough);
    }
  }, [adData, hasEnded, fireTracking, fireTrackingEvent]);
  
  // Gérer le skip
  const handleSkip = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!canSkip || hasEnded) return;
    
    console.log('⏭️ SKIP PUB:', adData?.id);
    setHasEnded(true);
    setIsPlaying(false);
    setDebugInfo(prev => prev + ' | Skippée');
    
    fireTrackingEvent('skip');
    fireTrackingEvent('close');
    
    onAdSkip?.();
  }, [canSkip, hasEnded, fireTrackingEvent, onAdSkip, adData?.id]);

  // Gérer la fin
  const handleAdEnd = useCallback(() => {
    if (hasEnded) return;
    
    console.log('✅ PUB TERMINÉE:', adData?.id);
    setHasEnded(true);
    setIsPlaying(false);
    setDebugInfo(prev => prev + ' | Terminée');
    
    fireTrackingEvent('complete');
    fireTrackingEvent('close');
    
    onAdComplete?.();
  }, [hasEnded, fireTrackingEvent, onAdComplete, adData?.id]);

  // Handlers ReactPlayer
  const handleReady = useCallback(() => {
    console.log('📺 PLAYER PRÊT:', adData?.id);
    setIsReady(true);
    setDebugInfo(prev => prev + ' | Player: Prêt');
    
    fireTrackingEvent('loaded');
  }, [adData?.id, fireTrackingEvent]);

  const handleStart = useCallback(() => {
    console.log('▶️ LECTURE DÉMARRÉE:', adData?.id);
    setIsPlaying(true);
    setDebugInfo(prev => prev + ' | Lecture: Démarrée');
    
    if (!hasStarted) {
      setHasStarted(true);
      // Fire les events de démarrage immédiatement
      fireTrackingEvent('start');
      fireTrackingEvent('creativeView');
    }
  }, [hasStarted, fireTrackingEvent, adData?.id]);

  const handleProgress = useCallback(({ playedSeconds, played }) => {
    if (hasEnded) return;
    
    setCurrentTime(playedSeconds);
    
    // Skip disponible
    if (playedSeconds >= skipDelay && !canSkip) {
      console.log(`⏰ SKIP DISPONIBLE après ${skipDelay}s pour pub:`, adData?.id);
      setCanSkip(true);
      setDebugInfo(prev => prev + ' | Skip: Dispo');
    }
    
    // Quartiles
    if (duration > 0 && playedSeconds > 0) {
      const progress = played;
      
      if (progress >= 0.25 && !trackingFiredRef.current.has(`${adData.id}_firstQuartile`)) {
        console.log('📊 QUARTILE 25% pour pub:', adData.id);
        fireTrackingEvent('firstQuartile');
      }
      if (progress >= 0.5 && !trackingFiredRef.current.has(`${adData.id}_midpoint`)) {
        console.log('📊 QUARTILE 50% pour pub:', adData.id);
        fireTrackingEvent('midpoint');
      }
      if (progress >= 0.75 && !trackingFiredRef.current.has(`${adData.id}_thirdQuartile`)) {
        console.log('📊 QUARTILE 75% pour pub:', adData.id);
        fireTrackingEvent('thirdQuartile');
      }
    }
  }, [duration, skipDelay, canSkip, hasEnded, fireTrackingEvent, adData?.id]);

  const handleDuration = useCallback((dur) => {
    console.log('⏱️ DURÉE DÉTECTÉE:', dur, 'secondes pour', adData?.id);
    setDuration(dur);
    setDebugInfo(prev => prev + ` | Durée: ${dur}s`);
  }, [adData?.id]);

  const handleError = useCallback((error) => {
    console.error('❌ ERREUR PLAYER:', error, 'pour pub:', adData?.id);
    setDebugInfo(prev => prev + ' | Erreur: ' + (error?.message || 'Inconnue'));
    setVideoError(true);
    
    // Fire error
    if (adData?.errors) {
      const errorCode = error?.code || error?.type || 'PLAYBACK_ERROR';
      const errorUrls = adData.errors.map(url => 
        url.replace('[ERRORCODE]', encodeURIComponent(errorCode))
      );
      fireTracking(errorUrls, 'error');
    }
    
    fireTrackingEvent('error');
    
    // Terminer après erreur
    setTimeout(() => {
      console.log('🔄 FERMETURE après erreur:', adData?.id);
      onAdComplete?.();
    }, 2000);
  }, [adData, fireTracking, fireTrackingEvent, onAdComplete]);

  const toggleMute = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted(!muted);
    
    console.log(`🔊 ${!muted ? 'MUTE' : 'UNMUTE'} pub:`, adData?.id);
    setDebugInfo(prev => prev + ` | Son: ${!muted ? 'Off' : 'On'}`);
    
    if (!muted) {
      fireTrackingEvent('mute');
    } else {
      fireTrackingEvent('unmute');
    }
  }, [muted, fireTrackingEvent, adData?.id]);

  // Reset complet pour nouvelle pub
  useEffect(() => {
    if (adData) {
      console.log('🔄 RESET COMPLET pour nouvelle pub:', adData.id);
      
      setCurrentTime(0);
      setCanSkip(false);
      setHasStarted(false);
      setHasEnded(false);
      setIsPlaying(false);
      setMuted(false);
      setVideoError(false);
      setIsReady(false);
      
      // Reset des états de visibilité
      setIsActuallyVisible(false);
      setImpressionSent(false);
      
      autoplayAttemptedRef.current = false;
      trackingFiredRef.current.clear();
    }
  }, [adData?.id]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (visibilityObserverRef.current) {
        visibilityObserverRef.current.disconnect();
      }
    };
  }, []);

  // Style du conteneur
  const containerStyle = {
    height: `calc(100vh - ${adHeight}px)`,
  };

  // Pas de publicité
  if (!adData) {
    return (
      <div className={`vast-ad-container ${className}`} style={containerStyle}>
        <div className="ad-loading">
          <div className="ad-loading-spinner"></div>
          <div>Chargement de la publicité...</div>
        </div>
      </div>
    );
  }

  // Pas d'URL vidéo (erreur du manager)
  if (!videoUrl) {
    return (
      <div className={`vast-ad-container ${className}`} style={containerStyle}>
        <div className="ad-error">
          <div>URL vidéo non disponible</div>
          <div style={{ fontSize: '12px', margin: '10px 0', color: 'rgba(255,255,255,0.7)' }}>
            Le gestionnaire de publicités n'a pas pu préparer l'URL
          </div>
          <button 
            onClick={() => onAdComplete?.()} 
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Continuer
          </button>
        </div>
      </div>
    );
  }

  // Erreur de lecture vidéo
  if (videoError) {
    return (
      <div className={`vast-ad-container ${className}`} style={containerStyle}>
        <div className="ad-error">
          <div>Impossible de lire la publicité</div>
          <div style={{ fontSize: '12px', margin: '10px 0', color: 'rgba(255,255,255,0.7)' }}>
            Visible: {isActuallyVisible ? 'OUI' : 'NON'} | Impression: {impressionSent ? 'OUI' : 'NON'}
          </div>
          <button 
            onClick={() => onAdComplete?.()} 
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Continuer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`vast-ad-container ${className}`} 
      style={containerStyle}
      ref={containerRef}
    >
      {/* Debug info en développement */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '120px',
          left: '20px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px',
          fontSize: '11px',
          zIndex: 25,
          borderRadius: '4px',
          maxWidth: '300px'
        }}>
          Visible: {isActuallyVisible ? '✅' : '❌'} | 
          Impression: {impressionSent ? '✅' : '❌'} | 
          Lecture: {hasStarted ? '✅' : '❌'}
        </div>
      )}
      
      {/* Étiquette publicité */}
      <div className="ad-label">
        Publicité {impressionSent ? '✅' : '🔄'} {isMobile && '📱'}
      </div>
      
      {/* Bouton Skip */}
      <button 
        className={`ad-skip-button ${canSkip ? 'enabled' : 'disabled'}`}
        onClick={handleSkip}
        disabled={!canSkip || hasEnded}
        style={{ touchAction: 'manipulation' }}
      >
        {canSkip ? 'Ignorer' : `Ignorer dans ${Math.max(0, Math.ceil(skipDelay - currentTime))}s`}
      </button>
      
      {/* Contrôle du son */}
      <button 
        className="ad-volume-control" 
        onClick={toggleMute}
        style={{ touchAction: 'manipulation' }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
      
      {/* Overlay cliquable */}
      <div 
        className="ad-overlay" 
        onClick={handleAdClick}
        style={{ 
          touchAction: 'manipulation',
          cursor: 'pointer'
        }}
      />
      
      {/* Barre de progression */}
      {duration > 0 && hasStarted && (
        <div className="ad-progress">
          <div 
            className="ad-progress-bar" 
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      )}
      
      {/* Player vidéo */}
      <ReactPlayer
        ref={playerRef}
        url={videoUrl}
        playing={isActuallyVisible && isPlaying && !hasEnded}
        muted={muted}
        width="100%"
        height="100%"
        className="ad-video-player"
        onReady={handleReady}
        onStart={handleStart}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onEnded={handleAdEnd}
        onError={handleError}
        playsinline={true}
        config={{
          file: {
            attributes: {
              preload: isActuallyVisible ? 'auto' : 'metadata',
              crossOrigin: 'anonymous',
              playsInline: true,
              'webkit-playsinline': 'true',
              'x5-playsinline': 'true',
              'x5-video-player-type': 'h5',
              'x5-video-player-fullscreen': 'false'
            }
          }
        }}
      />
    </div>
  );
};

export { VastAdPlayer };